import "@tento-ui/init";
import "@tento-ui/components/shell-layout/shell-layout";
import "@tento-ui/brand/header/marketing-header";
import "../../lib/marketing/lona-marketing-footer";

import {
  DocsShell,
  initDocsShell,
} from "@tento-ui/components/docs-layout/docs-shell";
import { Documentation } from "@tento-ui/components/documentation/documentation";
import { DocsNav } from "@tento-ui/components/docs-nav/docs-nav";
import { Prosemirror } from "@tento-ui/ui/prosemirror-import";
import { CodeBlockView } from "@tento-ui/components/code/code-block-view";
import { LispExampleView } from "@tento-ui/components/lisp-example/lisp-example-view";
import { SheetLispExampleView } from "@tento-lona/sheets-ui";
import { LonaResources } from "../../lib/resources";

// Public product docs render plain markdown with syntax-highlighted code blocks.
// The one exception is the Formulas page: it's an interactive Lisp reference
// built from runnable <std-lisp-example> / <std-sheet-lisp-example> widgets, so
// that page gets the richer serde + node-views (the in-app debug tools still
// stay with the developer docs at /dev/docs).
async function contentOptionsForPath(
  info: DocsShell.PageInfo,
): Promise<Pick<Documentation.Props, "serde" | "nodeViews">> {
  const pm = await Prosemirror.get();
  if (info.slug === "formulas") {
    const serde = pm.Markdown.buildSchema([
      {
        id: "lisp_example",
        componentName: "std-lisp-example",
        attributeKeys: new Set(["code"]),
      },
      {
        id: "sheet_lisp_example",
        componentName: "std-sheet-lisp-example",
        attributeKeys: new Set(["code"]),
      },
    ], { fileRefs: false });
    return {
      serde,
      nodeViews: {
        lisp_example: (node) => new LispExampleView(node),
        sheet_lisp_example: (node) => new SheetLispExampleView(node),
        code_block: (node) => new CodeBlockView(node),
      },
    };
  }
  const serde = pm.Markdown.buildSchema([], { fileRefs: false });
  return {
    serde,
    nodeViews: {
      code_block: (node) => new CodeBlockView(node),
    },
  };
}

function welcomeMarkdown(libraries: DocsNav.NavLibrary[]): string {
  let markdown =
    "# Documentation\n\n" +
    "Learn how to use Lona — the calendar you build out of sheets and rows. " +
    "Pick a topic below or use the sidebar to get around.\n\n";
  for (const library of libraries) {
    for (const section of library.sections) {
      markdown += `## ${section.title}\n\n`;
      for (const item of section.children) {
        markdown += `- [${item.label}](${item.path})\n`;
      }
      markdown += "\n";
    }
  }
  return markdown;
}

function libraryIndexMarkdown(
  libraries: DocsNav.NavLibrary[],
  namespace: string,
): string {
  const library = libraries.find(
    (candidate) => candidate.namespace === namespace,
  );
  if (library == null) {
    return welcomeMarkdown(libraries);
  }

  let markdown = `# ${library.title}\n\n`;
  for (const section of library.sections) {
    markdown += `## ${section.title}\n\n`;
    for (const item of section.children) {
      markdown += `- [${item.label}](${item.path})\n`;
    }
    markdown += "\n";
  }
  return markdown;
}

async function loadMarkdown(
  _path: string,
  info: DocsShell.PageInfo,
  libraries: DocsNav.NavLibrary[],
): Promise<Option<string>> {
  if (info.libraryNamespace == null) {
    return welcomeMarkdown(libraries);
  }
  if (info.slug == null) {
    return libraryIndexMarkdown(libraries, info.libraryNamespace);
  }
  return LonaResources.root.text(
    `/docs/content/${info.libraryNamespace}/${info.slug}.md`,
  );
}

await initDocsShell({
  layoutId: "docs-layout",
  navId: "nav",
  contentId: "content",
  breadcrumbId: "breadcrumb",
  expandButtonId: "docs-sidebar-button",
  pathPrefix: "/docs",
  fallbackTitle: "Docs",
  titleSuffix: "Lona",
  titlePrefix: "",
  initialState: { kind: "scripts" },
  libraryIcon: null,
  defaultExpanded: true,
  geometry: {
    sidebarWidthPx: 340,
    mobileBreakpointPx: 768,
    mobileSidebarMarginPx: 24,
  },
  // No leading "Lona" crumb — the marketing-header logo already links home, so
  // the breadcrumb starts at "Docs" to avoid duplicating it.
  breadcrumbRoot: () => [],
  loadMarkdown,
  contentOptionsForPath,
});
